import constate from 'constate';
import { useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { isDefined, useLoggedInAuthContext } from 'common';

import { compareRoutePriority } from './utils';
/**
 * We want to limit useFlags import since we want to ensure single flag usage.
 * This page uses multiple flags, so we allow it here.
 */
// eslint-disable-next-line no-restricted-imports
import { useFlags } from '../../../../utils/flags';
import { type RouteLink, type RouteSection } from '../../common';
import { getFlowStudioRoutes } from '../../getRoutes';

export enum SectionState {
  Closed,
  Open,
}

function useSidebarNavigationRouteSections() {
  const { user, permissions, featureFlags, hasAnyAgentAccess, hasAnyAgentGrantPermission } =
    useLoggedInAuthContext();
  const location = useLocation();

  const launchDarklyFlags = useFlags();

  const navigationRouteSections = useMemo(() => {
    const mappedRoutes = getFlowStudioRoutes({
      featureFlags,
      user,
      permissions,
      launchDarklyFlags,
      hasAnyAgentAccess,
      hasAnyAgentGrantPermission,
    }).map((item) => {
      if ('sections' in item) {
        const routeSections: (RouteSection & { priorityOrder: number })[] = [];
        item.sections.forEach((section) => {
          routeSections.push({
            label: section.label || item.label,
            links: [...section.links].sort(compareRoutePriority),
            priorityOrder: section.priorityOrder ?? Infinity,
          });
        });
        return routeSections;
      }
      return {
        ...item,
        priorityOrder: item.priorityOrder ?? Infinity,
      };
    });
    const flattenedRoutes: ((RouteLink | RouteSection) & { priorityOrder: number })[] = [];

    for (const item of mappedRoutes) {
      if (Array.isArray(item)) {
        flattenedRoutes.push(...item);
      } else {
        flattenedRoutes.push(item);
      }
    }
    flattenedRoutes.sort(compareRoutePriority);
    return flattenedRoutes;
  }, [
    featureFlags,
    user,
    permissions,
    launchDarklyFlags,
    hasAnyAgentAccess,
    hasAnyAgentGrantPermission,
  ]);

  const navigationLinks = useMemo(() => {
    const links: (RouteLink & { section?: RouteSection })[] = [];
    for (const item of navigationRouteSections) {
      if ('links' in item) {
        links.push(
          ...item.links.map((link) => ({
            ...link,
            section: item,
          }))
        );
      } else {
        links.push(item);
      }
    }
    return links;
  }, [navigationRouteSections]);

  const bestMatchNavigationLink = useMemo(() => {
    const matchingSectionStats = navigationLinks
      .map((link) => {
        return {
          ...link,
          match: matchPath(location.pathname, { path: link.to }),
        };
      })
      .filter((stat) => isDefined(stat.match));

    if (matchingSectionStats.length === 0) {
      return undefined;
    }
    let longestMatch = matchingSectionStats[0];
    for (const stat of matchingSectionStats) {
      if (stat.match?.isExact) {
        return stat;
      }
      if (stat.to.length > longestMatch.to.length) {
        longestMatch = stat;
      }
    }

    return longestMatch;
  }, [location.pathname, navigationLinks]);

  function isBestMatchNavigationSection(section: RouteSection) {
    return (
      isDefined(bestMatchNavigationLink?.section) &&
      section.label === bestMatchNavigationLink.section.label &&
      section.priorityOrder === bestMatchNavigationLink.section.priorityOrder
    );
  }

  const [sectionExpansionState, setSectionExpansionState] = useState<Record<string, SectionState>>(
    () => {
      const sectionState: Record<string, SectionState> = {};
      let isAnySectionOpen = false;
      navigationRouteSections.forEach((section) => {
        if ('links' in section) {
          if (isBestMatchNavigationSection(section)) {
            sectionState[section.label] = SectionState.Open;
            isAnySectionOpen = true;
          } else {
            sectionState[section.label] = SectionState.Closed;
          }
        }
      });
      if (!isAnySectionOpen && navigationRouteSections.length > 0) {
        sectionState[navigationRouteSections[0].label] = SectionState.Open;
      }
      return sectionState;
    }
  );

  return {
    navigationRouteSections,
    navigationLinks,
    sectionExpansionState,
    setSectionExpansionState,
    bestMatchNavigationLink,
    isBestMatchNavigationSection,
  };
}

export const [SidebarNavigationRouteSectionsProvider, useSidebarNavigationRouteSectionsContext] =
  constate(useSidebarNavigationRouteSections);
